/**
 * Keys Page Component
 * 
 * Page for managing parser API keys. Keys are used to authenticate
 * and identify transaction parsing requests from APLA workers.
 * 
 * Key Types:
 * - UTR: Uses UTR (Unique Transaction Reference) number
 * - LAST_TRN: Uses the last transaction method
 * - ORDER_ID: Uses order ID for matching
 * 
 * Features:
 * - View all keys in a table (with obfuscated values)
 * - Create new keys for specific banks
 * - Delete keys
 */
import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Alert } from 'antd';
import { getFullKeyList, createKey, deleteKey, getBankNames } from '../../services/api';
import styles from './KeysPage.module.css';

const { Option } = Select;

const KeysPage = () => {
  // State management
  const [keys, setKeys] = useState([]);                   // Key list
  const [bankNames, setBankNames] = useState([]);         // Available bank names
  const [loading, setLoading] = useState(false);          // Loading state
  const [error, setError] = useState(null);               // Error message
  const [isModalVisible, setIsModalVisible] = useState(false);  // Create modal visibility
  const [form] = Form.useForm();                          // Ant Design form instance

  /**
   * Fetches all parser keys from the API.
   * Keys are returned with obfuscated values for security.
   */
  const fetchKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getFullKeyList();
      setKeys(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading data');
    }
    setLoading(false);
  };

  /**
   * Fetches available bank names for the dropdown.
   */
  const fetchBankNames = async () => {
    try {
      const response = await getBankNames();
      if (Array.isArray(response.data)) {
        setBankNames(response.data);
      } else {
        throw new Error('Invalid data format from API');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load bank names list');
    }
  };

  /**
   * Handles key creation form submission.
   */
  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await createKey(values);
      message.success('Key created successfully.');
      fetchKeys();
      setIsModalVisible(false);
      form.resetFields();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error creating key.');
    }
  };

  /**
   * Handles key deletion.
   * @param {number} keyId - ID of the key to delete
   */
  const handleDelete = async (keyId) => {
    try {
      await deleteKey(keyId);
      message.success('Key deleted successfully.');
      fetchKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error deleting key.');
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchKeys();
    fetchBankNames();
  }, []);

  // Table column definitions
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Bank Name',
      dataIndex: 'bank_name',
      key: 'bank_name',
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Button
          className={styles.deleteButton}
          onClick={() => handleDelete(record.id)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2>Key Management</h2>
        <Button
          className={styles.addButton}
          onClick={() => setIsModalVisible(true)}
        >
          Add Key
        </Button>
      </div>

      {/* Error display */}
      {error && (
        <Alert
          className={styles.errorAlert}
          message="Error"
          description={typeof error === 'string' ? error : JSON.stringify(error)}
          type="error"
          showIcon
          closable
        />
      )}

      {/* Keys table */}
      <Table
        dataSource={keys}
        columns={columns}
        loading={loading}
        rowKey="id"
        className={styles.table}
      />

      {/* Create key modal */}
      <Modal
        title="Add Key"
        visible={isModalVisible}
        onOk={handleCreate}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} layout="vertical" className={styles.modalForm}>
          <Form.Item
            name="bank_name"
            label="Bank Name"
            rules={[{ required: true, message: 'Select a bank name' }]}
          >
            <Select placeholder="Select a bank">
              {bankNames.map((name, index) => (
                <Option key={index} value={name}>
                  {typeof name === 'string' ? name.toUpperCase() : JSON.stringify(name)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="key"
            label="Key"
            rules={[{ required: true, message: 'Enter a key' }]}
          >
            <Input placeholder="Enter key" />
          </Form.Item>
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Select a key type' }]}
          >
            <Select placeholder="Select type">
              <Option value="utr">UTR</Option>
              <Option value="last_trn">LAST_TRN</Option>
              <Option value="order_id">ORDER_ID</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default KeysPage;
